import React, { useCallback, useContext, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';
import ItemContent from '../../components/ItemContent/ItemContent';
import { IEditForm } from '../../types/interfaces/IEditForm';
import { NSCruiseLines } from '../../types/interfaces/NSCruiseLines';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { EPaths } from '../../types/enums/EPaths';
import { SessionContext } from '../../contexts/session/SessionContext';
import { endpoints } from '../../utils/endpoints';
import useApi from '../../utils/useApi';
import { EStatus } from '../../types/enums/EStatus';
import { ETables } from '../../types/enums/ETables';
import useColumnsHeaders from '../../utils/useColumnsHeaders';
import Table from '../../components/TableWithState/Table/Table';
import useStyle from '../../components/TableWithState/TableWithState.style';

const CruiseLineShipsTable: React.FC<{ cruiseLineId: string }> = ({ cruiseLineId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const styles = useStyle();
  const { data: cruiseLineData, isFetching } = useApi<NSCruiseLines.ICruiseLineDetails>(
    endpoints.cruiseLine(cruiseLineId),
  );

  const tableData = useMemo(
    () =>
      (cruiseLineData?.cruiseShips || []).map((ship) => ({
        _id: ship._id,
        name: ship.name,
      })),
    [cruiseLineData],
  );

  const columns = useColumnsHeaders([{ accessorAsHeader: 'name' }]);

  const handleCellClick = useCallback(
    (cell: any) => {
      history.push(`${EPaths.CruiseShips}/${cell.row.original._id}`);
    },
    [history],
  );

  if (isFetching) {
    return <CircularProgress />;
  }

  return (
    <Box className={styles.contentContainer} mt={4}>
      <Box className={styles.headerContainer}>
        <Typography variant="h4">{t(tPaths.sideMenu.allShips)}</Typography>
        <Box marginLeft={1}>
          <Typography variant="h4">{tableData.length}</Typography>
        </Box>
      </Box>
      {tableData.length ? (
        <Box className={styles.container}>
          <Table tableName={ETables.CruiseShips} data={tableData} columns={columns} handleCellClick={handleCellClick} />
        </Box>
      ) : (
        <Typography>{t(tPaths.tablePaging.noRecords)}</Typography>
      )}
    </Box>
  );
};

const EditCruiseLine = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { permissions } = useContext(SessionContext);

  const contentMapper = useCallback(
    (data: NSCruiseLines.ICruiseLineDetails): IEditForm[][] => [
      [
        {
          type: 'input',
          fieldName: 'name',
          label: t(tPaths.editFormLabels.name),
          initialValue: data.name?.en || Object.values(data.name || {})[0] || '',
        },
        {
          type: 'input',
          fieldName: 'description',
          label: t(tPaths.editFormLabels.description),
          initialValue: data.description?.en || Object.values(data.description || {})[0] || '',
          multiline: true,
        },
      ],
    ],
    [t],
  );

  const updateDataFormatter = useCallback((fields: any) => {
    const body: Record<string, unknown> = {
      name: { en: fields.name?.value?.trim() },
      status: fields.status ? 'Active' : 'Inactive',
    };

    if (fields.description?.value?.trim()) {
      body.description = { en: fields.description.value.trim() };
    }

    return body;
  }, []);

  const switches = useMemo(() => [{ key: 'status', label: t(tPaths.itemContent.active) }], [t]);

  const switchesDataMapper = useCallback((data: NSCruiseLines.ICruiseLineDetails) => {
    const current = data.status?.current;
    return { status: current === 'Active' || current === EStatus.Active };
  }, []);

  return (
    <>
      <ItemContent
        itemId={id}
        title={t(tPaths.itemContent.editCruiseLine)}
        path={EPaths.CruiseLines}
        contentMapper={contentMapper}
        updateDataFormatter={updateDataFormatter}
        switches={switches}
        switchesDataMapper={switchesDataMapper}
        editable={permissions.packages.edit}
        apiUrl={endpoints.cruiseLine(id)}
      />
      <CruiseLineShipsTable cruiseLineId={id} />
    </>
  );
};

export default EditCruiseLine;
